import { useCallback } from 'react';
import { useStore } from '../store';
import { send } from '../ws';
import { getWs } from '../wsConnection';
import { waitForAck } from '../utils/waitForAck';
import { correlationId } from "@interlucent/ids";
import type {
  ProfileCreateReq,
  ProfileUpdateReq,
  ProfileCreateAck,
  ProfileUpdateAck,
  AckPayload
} from '@interlucent/organization-protocol';
import { profileService } from '../services/profileService';

/**
 * Custom hook for profile CRUD operations
 * Abstracts WebSocket commands and store updates
 */
export function useProfiles(projectId: string) {
  const profiles = useStore(state => state.profiles);

  // Get profiles for the current project
  const projectProfiles = profileService.getProjectProfiles(projectId, profiles);

  /**
   * Create a new deployment profile
   */
  const createProfile = useCallback(async (
    data: Omit<ProfileCreateReq, 'projectId'>
  ): Promise<{ success: boolean; profileId?: string; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: ProfileCreateReq = {
        ...data,
        projectId
      };

      send('profile.create', payload, corr);

      const ack = await waitForAck<ProfileCreateAck>(corr);

      if (ack.ok) {
        return {
          success: true,
          profileId: ack.id
        };
      } else {
        return {
          success: false,
          error: 'Failed to create profile'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create profile'
      };
    }
  }, [projectId]);

  /**
   * Update an existing deployment profile
   */
  const updateProfile = useCallback(async (
    profileId: string,
    updates: Omit<ProfileUpdateReq, 'id'>
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: ProfileUpdateReq = {
        ...updates,
        id: profileId
      };

      send('profile.update', payload, corr);

      const ack = await waitForAck<ProfileUpdateAck>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to update profile'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update profile'
      };
    }
  }, []);

  /**
   * Delete a deployment profile
   */
  const deleteProfile = useCallback(async (
    profileId: string,
    force: boolean = false
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      send('profile.delete', { profileId }, corr);

      const ack = await waitForAck<AckPayload>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return {
          success: false,
          error: ack.reason || 'Failed to delete profile'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete profile'
      };
    }
  }, []);

  /**
   * Toggle profile active status
   */
  const toggleProfileStatus = useCallback(async (
    profileId: string,
    isActive: boolean
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();

      if (isActive) {
        send('profile.activate', { profileId }, corr);
      } else {
        send('profile.deactivate', { profileId }, corr);
      }

      const ack = await waitForAck<AckPayload>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to toggle profile status'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to toggle profile status'
      };
    }
  }, []);

  /**
   * Force push pool config to PoolDO
   */
  const forcePushConfig = useCallback(async (
    profileId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      send('profile.forcePushConfig', { profileId }, corr);
      const ack = await waitForAck<AckPayload>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return { success: false, error: ack.reason || 'Failed to push config' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to push config' };
    }
  }, []);

  /**
   * Generate a worker admission token for a profile/pool (platform admin only)
   */
  const generateWorkerToken = useCallback(async (
    profileId: string,
    expiresInSeconds?: number
  ): Promise<{ success: boolean; token?: string; kid?: string; expiresInSeconds?: number; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      send('profile.generateWorkerToken', {
        profileId,
        ...(expiresInSeconds != null && { expiresInSeconds }),
      }, corr);

      const ack = await waitForAck<AckPayload & { token: string; kid: string; expiresInSeconds: number }>(corr);

      if (ack.ok) {
        return {
          success: true,
          token: ack.token,
          kid: ack.kid,
          expiresInSeconds: ack.expiresInSeconds,
        };
      } else {
        return { success: false, error: ack.reason || 'Failed to generate worker token' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to generate worker token' };
    }
  }, []);

  /**
   * Get active profiles for the project
   */
  const activeProfiles = profileService.getActiveProfiles(projectId, profiles);

  /**
   * Check if a profile name is unique
   */
  const isNameUnique = useCallback((name: string, excludeId?: string) => {
    return profileService.isNameUnique(name, projectId, profiles, excludeId);
  }, [projectId, profiles]);

  /**
   * Check if a profile slug is unique
   */
  const isSlugUnique = useCallback((slug: string, excludeId?: string) => {
    return profileService.isSlugUnique(slug, projectId, profiles, excludeId);
  }, [projectId, profiles]);

  return {
    profiles: projectProfiles,
    activeProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    toggleProfileStatus,
    forcePushConfig,
    generateWorkerToken,
    isNameUnique,
    isSlugUnique
  };
}
